import { useState } from "react";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { upcomingHolidays } from "@/components/TimetableSection";
import { trackEvent } from "@/utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { parent_name: "", email: "", child_age: "", holidays: [] };

function HolidayOption({ name, index, checked, onToggle }) {
  return (
    <label
      htmlFor={`reminder-holiday-${index}`}
      className={`flex items-center gap-3 rounded border px-3 py-2.5 cursor-pointer transition-colors ${checked ? "border-[#D4A857] bg-[#D4A857]/15" : "border-white/20 hover:border-white/40"}`}
    >
      <Checkbox
        id={`reminder-holiday-${index}`}
        checked={checked}
        onCheckedChange={() => onToggle(name)}
        className="border-white/60 data-[state=checked]:bg-[#D4A857] data-[state=checked]:border-[#D4A857] data-[state=checked]:text-[#0A1A2F]"
        data-testid={`reminder-holiday-checkbox-${index}`}
      />
      <span className="text-sm text-white font-['Lato']">{name}</span>
    </label>
  );
}

export default function HolidayReminderSection() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) => {
    setDone(false);
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };
  const toggle = (name) =>
    setForm((f) => ({
      ...f,
      holidays: f.holidays.includes(name) ? f.holidays.filter((h) => h !== name) : [...f.holidays, name],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.holidays.length === 0) {
      toast.error("Please tick at least one holiday.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reminders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, child_age: Number(form.child_age) }),
      });
      if (!res.ok) throw new Error();
      trackEvent("reminder_signup", form.holidays.join(", "));
      toast.success("You're on the list! We'll let you know when booking opens.");
      setForm(EMPTY);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reminders" className="py-20 bg-[#0A1A2F] scroll-mt-24" data-testid="holiday-reminder-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2 text-white">
          <div className="inline-flex items-center gap-2 bg-[#D4A857]/15 text-[#D4A857] px-4 py-2 rounded text-sm font-bold uppercase tracking-wide mb-4 font-['Montserrat']">
            <Bell size={16} />
            Holiday Reminders
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 font-['Montserrat']">
            Never miss <span className="text-[#D4A857]">booking day</span>
          </h2>
          <p className="text-white/80 font-['Lato'] leading-relaxed">
            Popular dates sell out fast. Tell us which holidays you're interested in and we'll email you as soon as booking opens.
          </p>
          {done && (
            <p className="mt-6 text-[#D4A857] font-bold font-['Montserrat']" data-testid="reminder-success-message">
              Thanks! You're on the reminder list.
            </p>
          )}
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white/5 border border-white/15 rounded-lg p-6 space-y-5" data-testid="reminder-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1 space-y-2">
              <Label htmlFor="reminder-name" className="text-white font-['Montserrat']">Parent name</Label>
              <Input id="reminder-name" name="parent_name" required value={form.parent_name} onChange={update} className="bg-white" data-testid="reminder-name-input" />
            </div>
            <div className="sm:col-span-1 space-y-2">
              <Label htmlFor="reminder-email" className="text-white font-['Montserrat']">Email</Label>
              <Input id="reminder-email" name="email" type="email" required value={form.email} onChange={update} className="bg-white" data-testid="reminder-email-input" />
            </div>
            <div className="sm:col-span-1 space-y-2">
              <Label htmlFor="reminder-age" className="text-white font-['Montserrat']">Child's age</Label>
              <Input id="reminder-age" name="child_age" type="number" min={3} max={18} required value={form.child_age} onChange={update} className="bg-white" data-testid="reminder-age-input" />
            </div>
          </div>

          <div>
            <p className="text-white text-sm font-bold mb-3 font-['Montserrat']">Which holidays are you interested in?</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {upcomingHolidays.map((h, i) => (
                <HolidayOption key={h.name} name={h.name} index={i} checked={form.holidays.includes(h.name)} onToggle={toggle} />
              ))}
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#D4A857] hover:bg-[#C49A4D] text-[#0A1A2F] font-bold py-6 rounded font-['Montserrat'] uppercase tracking-wide"
            data-testid="reminder-submit-button"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : "Remind me when booking opens"}
          </Button>
        </form>
      </div>
    </section>
  );
}
